import { useState } from "react";
import { useLocalStorage } from "react-use";
import { ShellContent } from "../../shell/Shell.jsx";
import tokens from "../../../design-system/tokens.js";
import Layout from "../../../design-system/components/Layout/Layout.jsx";
import { useWorkshopTourContext } from "../user-tour/context/WorkshopUserTourContext.jsx";
import { MODELS, useModel } from "../../../api";
import { Dropdown } from "../../../design-system/components/Dropdown/Dropdown.jsx";
import { styles } from "./sparkOverview.css.js";
import { StageSchedule } from "./components/StageSchedule/StageSchedule.jsx";
import { HighLevelNavigation } from "./components/HighLevelNavigation/HighLevelNavigation.jsx";
import { Title } from "./components/Title/Title.jsx";

/**
 * Component for displaying a Spark overview page with data.
 * Mainly this helps with navigating in a spark.
 * A spark has Phases (Discover, Create, Share), have Stages, have Procedures and Stations, Stations have Steps.
 *
 * @param baseUrl - The spark's base URL that step links are built from.
 * @param sparkSlug
 * @param {Object[]} phases - The phases list.
 * @param {string} templateTitle - The Spark's title.'
 * @param {boolean} disableSome
 * @returns {JSX.Element} - The SparkOverviewWithData component.
 */
export const SparkOverviewWithData = ({
  baseUrl,
  sparkSlug,
  phases = [],
  templateTitle,
  disableSome = false,
}) => {
  // Save current phaseIndex and currentStageIndex in localStorage to remember where we were
  const path =
    typeof window !== "undefined" ? window.location.pathname : "unknown";
  const [currentPhaseIndex, setCurrentPhaseIndex] = useLocalStorage(
    `lastPhaseIndex-${path}`,
    0,
  );
  const [currentStageIndex, setCurrentStageIndex] = useLocalStorage(
    `lastStageIndex-${path}`,
    0,
  );
  const [currentPhase, setCurrentPhase] = useState(
    phases[currentPhaseIndex] ?? phases[0],
  );
  const [currentStage, setCurrentStage] = useState(
    phases[currentPhaseIndex]?.stages[currentStageIndex] ??
      phases[0]?.stages[0],
  );
  const modules = useModel(
    MODELS.SPARK_SCHEDULE_STATIONS_AND_PROCEDURES(
      sparkSlug,
      currentStage?.slug,
    ),
  );

  // Adjust the layout to keep the UserTour from looking buggy
  const {
    state: { tourActive },
  } = useWorkshopTourContext();

  function handleStageClick(phase, stage) {
    setCurrentPhase(phase);
    const phaseIndex = phases.findIndex(
      (p) => p.name === phase.name && p.type === phase.type,
    );
    if (phaseIndex >= 0) setCurrentPhaseIndex(phaseIndex);

    setCurrentStage(stage);
    const stageIndex = phase.stages.findIndex((s) => s.slug === stage.slug);
    if (stageIndex >= 0) setCurrentStageIndex(stageIndex);
  }

  return (
    <ShellContent bg={tokens.color.full.gray800}>
      <Layout userTour={tourActive} isDarkMode={true}>
        <div className={styles.outerContainer}>
          <div className={styles.headerContainer}>
            <Title title={templateTitle} />
            <Dropdown withLogo={false} avatarSize="50px" />
          </div>
          <div className={styles.innerContainer}>
            <div className={styles.leftBox}>
              <HighLevelNavigation
                phases={phases}
                currentPhase={currentPhase}
                currentStage={currentStage}
                handleStageClick={handleStageClick}
                disableSome={disableSome}
              />
            </div>
            <div className={styles.rightBox}>
              <StageSchedule
                modules={modules}
                baseUrl={baseUrl}
                disableSome={disableSome}
              />
            </div>
          </div>
        </div>
      </Layout>
    </ShellContent>
  );
};
